import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, money } from "@/lib/api";

const EMPTY = { name: "", phone: "", email: "", tier: "standard", notes: "" };
const field = "h-11 bg-slate-950/60 border-slate-700";

export const QuickLeadDialog = ({ open, onOpenChange }) => {
  const [f, setF] = useState(EMPTY);
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && !tiers) api.get("/public/pricing").then((r) => setTiers(r.data)).catch(() => {}); }, [open, tiers]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.phone.trim() && !f.email.trim()) return toast.error("Add a phone number or an email");
    setBusy(true);
    try {
      await api.post("/pricing-requests/quick", { ...f, email: f.email.trim() || null });
      toast.success(`Lead saved: ${f.name}`);
      window.dispatchEvent(new Event("pricing-requests:changed"));
      setF(EMPTY);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err, "Could not save the lead")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md" data-testid="quick-lead-dialog">
        <DialogHeader className="text-left"><DialogTitle className="font-display">Quick lead</DialogTitle><DialogDescription>Saved to New requests on Studies so you can convert it later.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input required autoFocus data-testid="quick-lead-name" value={f.name} onChange={set("name")} placeholder="Name *" className={field} />
          <Input data-testid="quick-lead-phone" type="tel" inputMode="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} placeholder="Phone" className={field} />
          <Input data-testid="quick-lead-email" type="email" inputMode="email" value={f.email} onChange={set("email")} placeholder="Email" className={field} />
          <Select value={f.tier} onValueChange={(v) => setF((x) => ({ ...x, tier: v }))}>
            <SelectTrigger data-testid="quick-lead-tier" className={field}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              {(tiers?.tiers || [{ key: "standard", label: "Standard" }]).map((t) => <SelectItem key={t.key} value={t.key} data-testid={`quick-lead-tier-${t.key}`}>{t.label}{t.price != null ? ` · ${money(t.price, tiers.currency)}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Textarea data-testid="quick-lead-notes" rows={3} maxLength={1000} value={f.notes} onChange={set("notes")} placeholder="Short note (what they need, where you met)" className="bg-slate-950/60 border-slate-700" />
          <Button type="submit" data-testid="quick-lead-submit" disabled={busy || !f.name.trim()} className="w-full h-11 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}Save lead</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
